import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, Globe2, Layers, Clock } from "lucide-react";
import { useContent } from "@/content";

const BADGE_ICONS = [Globe2, Layers, Clock];

const MaskedLine = ({ text, delay, gold }) => (
  <span className="mask-line">
    <motion.span
      className={`block text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] ${gold ? "text-amber-500" : "text-[#0B1528]"}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

const FloatBadge = ({ icon: Icon, text, className, delay = "0s", testid }) => (
  <div className={`absolute animate-float-soft ${className}`} style={{ animationDelay: delay }} data-testid={testid}>
    <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white/95 backdrop-blur px-4 py-2 shadow-lg shadow-slate-900/5">
      <Icon className="h-3.5 w-3.5 text-amber-500" />
      <span className="font-mono text-[10px] tracking-widest text-slate-600 uppercase whitespace-nowrap">{text}</span>
    </div>
  </div>
);

const Emblem = ({ mx, my, badges }) => {
  const rx = useTransform(my, [-0.5, 0.5], [8, -8]);
  const ry = useTransform(mx, [-0.5, 0.5], [-8, 8]);
  const tx = useTransform(mx, [-0.5, 0.5], [-14, 14]);
  const ty = useTransform(my, [-0.5, 0.5], [-14, 14]);
  return (
  <motion.div
    initial={{ opacity: 0, scale: 0.85 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 1.1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
    className="relative mx-auto mt-16 md:mt-20 h-[340px] w-[340px] sm:h-[420px] sm:w-[420px]"
    style={{ perspective: 1200 }}
    data-testid="hero-emblem"
  >
    {/* orbit rings */}
    <div className="animate-spin-slow absolute inset-0 rounded-full border border-dashed border-slate-300">
      <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-amber-400 shadow-[0_0_16px_rgba(255,159,28,0.7)]" />
      <span className="absolute top-1/2 -right-1.5 -translate-y-1/2 h-2.5 w-2.5 rounded-full bg-blue-500 shadow-[0_0_14px_rgba(37,99,235,0.6)]" />
    </div>
    <div className="animate-spin-reverse absolute inset-8 sm:inset-10 rounded-full border border-slate-200 bg-white/40">
      <span className="absolute -bottom-1 left-1/3 h-2 w-2 rounded-full bg-[#0B1528]" />
      <span className="absolute top-6 right-8 h-2 w-2 rounded-full bg-amber-400 animate-pulse-dot" />
    </div>
    <div className="absolute inset-20 sm:inset-24 rounded-full border border-slate-200/70 bg-gradient-to-b from-white to-slate-50 shadow-inner" />

    {/* center emblem card */}
    <motion.div style={{ x: tx, y: ty, rotateX: rx, rotateY: ry }} className="absolute inset-0 flex items-center justify-center">
      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="rounded-[2rem] bg-white p-6 sm:p-8 shadow-[0_30px_80px_-20px_rgba(11,21,40,0.25)] border border-slate-100"
      >
        <img src="/assets/logo-mark.png" alt="Desantrix emblem" className="h-24 w-24 sm:h-32 sm:w-32 object-contain" />
      </motion.div>
    </motion.div>

    <FloatBadge icon={BADGE_ICONS[0]} text={badges[0]} className="-top-3 -left-6 sm:-left-14" testid="hero-badge-global" />
    <FloatBadge icon={BADGE_ICONS[1]} text={badges[1]} className="top-1/3 -right-8 sm:-right-16" delay="1.4s" testid="hero-badge-capabilities" />
    <FloatBadge icon={BADGE_ICONS[2]} text={badges[2]} className="-bottom-2 left-1/2 -translate-x-1/2" delay="2.4s" testid="hero-badge-delivery" />
  </motion.div>
  );
};

export const Hero = ({ onNavigate }) => {
  const { HERO } = useContent();
  const ref = useRef(null);
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const mx = useSpring(rawX, { stiffness: 60, damping: 20 });
  const my = useSpring(rawY, { stiffness: 60, damping: 20 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    rawX.set((e.clientX - r.left) / r.width - 0.5);
    rawY.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    rawX.set(0);
    rawY.set(0);
  };
  return (
  <section ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} id="home" className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden" data-testid="hero-section">
    <div className="hero-grid-light absolute inset-0" />
    <div className="glow-amber-light absolute -top-32 left-1/2 -translate-x-1/2 h-[560px] w-[560px] rounded-full" />
    <div className="glow-blue-light absolute bottom-0 -left-40 h-[480px] w-[480px] rounded-full" />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-8 text-center">
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="inline-flex items-center gap-2.5 rounded-full border border-slate-200 bg-white px-5 py-2 shadow-sm mb-8"
        data-testid="hero-eyebrow"
      >
        <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse-dot" />
        <span className="font-mono text-[10px] sm:text-xs tracking-[0.3em] text-slate-500 uppercase">
          {HERO.eyebrow}
        </span>
      </motion.div>

      <h1 data-testid="hero-headline">
        {HERO.lines.map((l, i) => (
          <MaskedLine key={l} text={l} delay={0.3 + i * 0.15} gold={i === HERO.lines.length - 1} />
        ))}
      </h1>

      <motion.p
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.9 }}
        className="mx-auto mt-7 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed"
        data-testid="hero-subcopy"
      >
        {HERO.sub}
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.05 }}
        className="mt-9 flex flex-wrap items-center justify-center gap-4"
      >
        <button
          onClick={() => onNavigate("contact")}
          data-testid="hero-quote-cta"
          className="group inline-flex items-center gap-2 rounded-full bg-[#0B1528] px-8 py-4 text-base font-semibold text-white hover:bg-amber-500 hover:text-[#0B1528] active:scale-95 transition-all duration-300"
        >
          {HERO.primaryCta}
          <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>
        <button
          onClick={() => onNavigate("services")}
          data-testid="hero-services-cta"
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-8 py-4 text-base font-medium text-slate-700 hover:border-amber-500/70 hover:text-amber-600 active:scale-95 transition-all duration-300"
        >
          {HERO.secondaryCta}
        </button>
      </motion.div>

      <Emblem mx={mx} my={my} badges={HERO.badges} />
    </div>
  </section>
  );
};
